import { Api } from '@/api/api'
import { Endpoint, HttpMethod } from '@/api/common'
import { waitUntilTrue } from '@/api/react-query'
import { customerToken, logIn, logOut } from '@/utils/auth'
import { getBackendUrl, getPublishableKey, isLoginRequired } from '@/utils/tester-mode'
import {
	applyRequestHeaders,
	shouldBootstrapAnonymousLogin,
	shouldLogoutOnAuthError,
	shouldWaitForInitialization
} from '@/utils/api-interceptors'
import { getRandomId } from '@/utils/utilities'
import Axios from 'axios'
import { BehaviorSubject } from 'rxjs'

export class FastTrackApi extends Api {
	private initializing = new BehaviorSubject(false)
	private initialized = new BehaviorSubject(false)

	constructor() {
		const client = Axios.create({
			baseURL: getBackendUrl()
		})

		client.interceptors.request.use(
			request => {
				const publishableKey = getPublishableKey()
				if (!publishableKey) {
					console.error('PUBLISHABLE_KEY is not set')
				}
				applyRequestHeaders(request.headers, {
					publishableKey,
					token: customerToken.value || undefined,
					requestId: getRandomId()
				})
				return request
			},
			error => {
				return Promise.reject(error)
			}
		)

		client.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config
				if (
					originalRequest &&
					shouldLogoutOnAuthError({
						url: originalRequest.url,
						status: error.response?.status,
						isAxiosError: Axios.isAxiosError(error),
						alreadyRetried: originalRequest._retry
					})
				) {
					originalRequest._retry = true
					try {
						logOut()
					} catch {
						// swallow logout errors
					}
				}
				return Promise.reject(error)
			}
		)

		super(client)

		this.initialize()
	}

	private async initialize() {
		if (this.initialized.value || this.initializing.value) {
			return
		}

		// Skip anonymous login when there is already a token or the tenant
		// requires an explicit account login.
		if (!shouldBootstrapAnonymousLogin({ hasToken: !!customerToken.value, loginRequired: isLoginRequired() })) {
			this.initialized.next(true)
			return
		}

		this.initializing.next(true)
		const tokenResponse = await this.customerLoginNoThrow({})
		if (tokenResponse.isSuccess) {
			const token = tokenResponse.response.token
			customerToken.next(token)
			const customerResponse = await this.getCustomerNoThrow({})
			if (customerResponse.isSuccess) {
				logIn(token, customerResponse.response)
			}
		}
		this.initializing.next(false)
		this.initialized.next(true)
	}

	override async onBeforeRequest<T extends Endpoint>(data: {
		endpoint: T
		input: any
		method: HttpMethod
		path: string
	}) {
		const mustWait = shouldWaitForInitialization({
			method: data.method,
			hasToken: !!customerToken.value,
			isCustomerLogin: data.endpoint === Endpoint.CustomerLogin
		})
		if (mustWait) {
			await waitUntilTrue(this.initialized)
		}
	}
}
